import React, { useEffect, useRef, useState } from 'react';
import { FormulaBlock } from '../../../core/ast/document.types';
import { Sigma, Sparkles, Wand2 } from 'lucide-react';
import { normalizeLatexFormula, renderSmartKatex } from '../../../core/ast/smartLatex';

interface FormulaBlockEditorProps {
  block: FormulaBlock;
  onUpdate: (updates: Partial<FormulaBlock['data']>) => void;
}

const SNIPPET_CATEGORIES = {
  electronics: [
    { label: 'Shockley Diode', latex: 'I_D = I_S \\left( e^{\\frac{V_D}{n V_T}} - 1 \\right)' },
    { label: "Ohm's Law", latex: 'V = I \\cdot R' },
    { label: 'Dynamic Resistance', latex: 'r_d = \\frac{\\Delta V_D}{\\Delta I_D} = \\frac{0.72\\text{ V} - 0.65\\text{ V}}{8.60\\text{ mA} - 2.45\\text{ mA}} \\approx 11.38\\,\\Omega' },
    { label: 'RC Cutoff', latex: 'f_c = \\frac{1}{2 \\pi R C}' },
    { label: 'Thermal Voltage', latex: 'V_T = \\frac{k T}{q} \\approx 26\\text{ mV}' },
    { label: 'Op-Amp Inverting Gain', latex: 'A_v = -\\frac{R_f}{R_{in}}' },
  ],
  physics: [
    { label: 'Kinematics', latex: 'v^2 = u^2 + 2 a s' },
    { label: 'Wave Frequency', latex: 'v = f \\cdot \\lambda' },
    { label: 'Photoelectric Effect', latex: 'E_k = h \\nu - \\Phi' },
    { label: 'Coulomb Force', latex: 'F = \\frac{1}{4 \\pi \\varepsilon_0} \\frac{|q_1 q_2|}{r^2}' },
  ],
  calculus: [
    { label: 'Definite Integral', latex: '\\int_{a}^{b} f(x)\\,dx = F(b) - F(a)' },
    { label: 'Derivative', latex: '\\frac{df}{dx} = \\lim_{h \\to 0} \\frac{f(x+h) - f(x)}{h}' },
    { label: 'Partial Derivative', latex: '\\frac{\\partial^2 \\psi}{\\partial x^2} + \\frac{\\partial^2 \\psi}{\\partial y^2} = 0' },
    { label: 'Summation', latex: '\\sum_{n=1}^{\\infty} \\frac{1}{n^2} = \\frac{\\pi^2}{6}' },
  ],
  symbols: [
    { label: 'α, β, γ', latex: '\\alpha, \\beta, \\gamma' },
    { label: 'θ, ω, φ', latex: '\\theta, \\omega, \\phi' },
    { label: 'Δ, Σ, Ω', latex: '\\Delta, \\Sigma, \\Omega' },
    { label: '±, ≈, ≠', latex: '\\pm, \\approx, \\neq' },
  ],
};

export const FormulaBlockEditor: React.FC<FormulaBlockEditorProps> = ({ block, onUpdate }) => {
  const previewRef = useRef<HTMLDivElement>(null);
  const [activeCategory, setActiveCategory] = useState<keyof typeof SNIPPET_CATEGORIES>('electronics');
  const { latex, displayMode = true, equationNumber, caption } = block.data;

  useEffect(() => {
    if (previewRef.current) {
      renderSmartKatex(latex || 'x = 0', previewRef.current, { displayMode: true });
    }
  }, [latex]);

  const handleNormalizeInput = () => {
    if (!latex) return;
    const cleaned = normalizeLatexFormula(latex);
    onUpdate({ latex: cleaned });
  };

  const insertSnippet = (snipLatex: string) => {
    onUpdate({ latex: snipLatex });
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
        padding: 'var(--space-3)',
        backgroundColor: 'var(--bg-surface-subtle)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-subtle)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 4 }}>
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <Sigma size={14} color="var(--primary-600)" />
          <span>Smart KaTeX Formula Editor</span>
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            className="btn btn-ghost btn-sm text-xs"
            style={{ fontSize: '11px', padding: '2px 8px', color: 'var(--primary-600)' }}
            onClick={handleNormalizeInput}
            title="Clean LaTeX syntax, unwrap delimiters, fix units and Greek symbols"
          >
            <Wand2 size={12} /> Auto-Clean LaTeX
          </button>
          <label
            style={{
              fontSize: 'var(--font-size-xs)',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={displayMode}
              onChange={(e) => onUpdate({ displayMode: e.target.checked })}
            />
            <span>Centered Display</span>
          </label>
        </div>
      </div>

      {/* LaTeX Input */}
      <div className="form-group">
        <label className="form-label" style={{ fontSize: '11px', display: 'flex', justifyContent: 'space-between' }}>
          <span>LaTeX / Math Expression (delimiters \( ... \), $, units, unicode Ω, Δ auto-handled)</span>
        </label>
        <textarea
          className="form-textarea font-mono text-xs"
          rows={2}
          value={latex}
          onChange={(e) => onUpdate({ latex: e.target.value })}
          onBlur={() => {
            // Auto-clean on blur if enclosing delimiters or unit issues exist
            if (latex && (/^\\\(|^\\\[|^\$\$|^\$|\\mathrm\{~|~|Ω|Δ|≈/g.test(latex))) {
              onUpdate({ latex: normalizeLatexFormula(latex) });
            }
          }}
          placeholder="e.g. \( r_{d}=\frac{\Delta V_{D}}{\Delta I_{D}}=\frac{0.72 \mathrm{~V}-0.65 \mathrm{~V}}{8.60 \mathrm{~mA}-2.45 \mathrm{~mA}} \approx 11.38 \Omega \)"
        />
      </div>

      {/* Formula Snippets Palette */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, borderBottom: '1px solid var(--border-subtle)', paddingBottom: 2 }}>
          <span style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Sparkles size={10} /> Presets:
          </span>
          {(['electronics', 'physics', 'calculus', 'symbols'] as const).map((cat) => (
            <button
              key={cat}
              type="button"
              className={`btn btn-ghost btn-sm text-xs ${activeCategory === cat ? 'active' : ''}`}
              style={{
                fontSize: '10px',
                padding: '1px 5px',
                fontWeight: activeCategory === cat ? 700 : 500,
                color: activeCategory === cat ? 'var(--primary-600)' : 'var(--text-secondary)',
              }}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', paddingTop: 2 }}>
          {SNIPPET_CATEGORIES[activeCategory].map((snip, i) => (
            <button
              key={i}
              type="button"
              className="btn btn-ghost btn-sm text-xs"
              style={{
                fontSize: '10px',
                padding: '2px 6px',
                border: '1px solid var(--border-subtle)',
                backgroundColor: 'var(--bg-surface)',
              }}
              onClick={() => insertSnippet(snip.latex)}
              title={`Insert ${snip.label}`}
            >
              + {snip.label}
            </button>
          ))}
        </div>
      </div>

      {/* Live KaTeX Render Preview Box */}
      <div
        style={{
          padding: 'var(--space-3)',
          backgroundColor: 'var(--bg-surface)',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-sm)',
          minHeight: '46px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflowX: 'auto',
        }}
      >
        <div ref={previewRef} />
      </div>

      {/* Label and Caption */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 'var(--space-2)' }}>
        <div className="form-group">
          <label className="form-label" style={{ fontSize: '11px' }}>Equation #</label>
          <input
            type="text"
            className="form-input text-xs"
            value={equationNumber || ''}
            onChange={(e) => onUpdate({ equationNumber: e.target.value })}
            placeholder="(Eq. 1)"
          />
        </div>
        <div className="form-group">
          <label className="form-label" style={{ fontSize: '11px' }}>Caption / Explanation</label>
          <input
            type="text"
            className="form-input text-xs"
            value={caption || ''}
            onChange={(e) => onUpdate({ caption: e.target.value })}
            placeholder="e.g. Diode forward bias equation"
          />
        </div>
      </div>
    </div>
  );
};
